<template>
  <section
    class="RunHeroView"
    data-testid="runHero"
    :data-theme="theme"
  >
    <div class="RunHeroView__layout">
      <header class="RunHeroView__header">
        <component
          :is="canNavigate ? 'RouterLink' : 'div'"
          class="RunHeroView__headerLink"
          :to="canNavigate ? getRouteForRun(run.id) : undefined"
          @click.prevent="canNavigate ? handleRunClick : undefined"
        >
          <CrewListLabel
            class="RunHeroView__crew"
            data-testid="crewList"
            tag="h2"
            :crews="modelValue.crews"
            :isClickable="isCrewClickable"
          />
          <h1
            class="RunHeroView__datetime"
          >
            <time
              class="RunHeroView__datetimeLabel"
              :datetime="run.start_at"
            >
              <div
                data-testid="day"
                class="RunHeroView__datetimeDay"
              >{{ formattedRelativeDay }}</div>
              <div
                class="RunHeroView__datetimeDateTime"
                data-testid="datetime"
                :data-size="isTimeLong ? 'small' : 'medium'"
              >
                {{ formattedShortDate }} <span class="RunHeroView__datetimeDateTimeSeparator">@</span> {{ formattedStartTime }}<template v-if="run.end_at && formattedEndTime"> – {{ formattedEndTime }}</template>
              </div>
            </time>
          </h1>
          <RunLocation
            v-if="includeLocation && run.court"
            class="RunHeroView__court"
            data-testid="court"
            tag="h2"
            :court="run.court"
          />
          <div
            v-if="$slots.corner"
            class="RunHeroView__corner"
          >
            <slot name="corner" />
          </div>
        </component>
      </header>

      <div
        v-if="$slots.footer"
        class="RunHeroView__footer"
      >
        <slot name="footer" />
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import CrewListLabel from "@/components/CrewListLabel.vue";
import RunLocation from "@/components/RunLocation.vue";
import useRunSchedule from "@/composables/useRunSchedule";
import getRouteForRun from "@/lib/utils/route/getRouteForRun";
import useModalStore from "@/stores/useModalStore";
import { CourtSummary, CrewSummary, RunSummary } from "@shared/lib/database/query.types";
import { computed } from "vue";

const run = defineModel<RunSummary & {
  crews: Array<CrewSummary>,
  court: CourtSummary | null,
}>({
  required: true,
});

const {
  theme = "dark",
  includeLocation = true,
  canNavigate = true,
  isCrewClickable = false,
} = defineProps<{
  includeLocation?: boolean,
  theme?: "light" | "dark",
  canNavigate?: boolean,
  isCrewClickable?: boolean,
}>();

const {
  openModal,
} = useModalStore();

const {
  formattedRelativeDay,
  formattedShortDate,
  formattedStartTime,
  formattedEndTime,
} = useRunSchedule(run);

const isTimeLong = computed(() => {
  return (formattedStartTime.value?.length || 0) > 4 || (formattedEndTime.value?.length || 0) > 4;
});

function handleRunClick (e: MouseEvent) {
  e.preventDefault();
  if (!canNavigate) return;

  openModal(getRouteForRun(run.value.id));
}
</script>

<style lang="scss" scoped>
.RunHeroView {
  position: relative;

  padding: 20px 16px;
  overflow: hidden;
  container-type: inline-size;
  container-name: runHeroHeader;

  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);

  &[data-theme="light"] {
    --localColorSecondaryText: currentColor;
    --localColorSecondaryFill: var(--themeColorNeutral400);
  }

  &[data-theme="dark"] {
    --localColorSecondaryText: var(--themeLightTransparent700);
    --localColorSecondaryFill: var(--themeLightTransparent700);
    padding-bottom: 24px;
    background-color: var(--themeColorPrimary);
  }
}

.RunHeroView__layout {
  display: grid;
  gap: min(24px, 7cqi);
  align-content: start;
}

.RunHeroView__header {
  display: grid;
}

.RunHeroView__headerLink {
  display: grid;
}

.RunHeroView__crew {
  margin-bottom: 14px;
  padding-right: 32px;
  color: var(--localColorSecondaryText);
}

.RunHeroView__datetimeLabel {
  display: grid;
  gap: 10px;
}

.RunHeroView__datetimeDay {
  font-size: 40px;
}

.RunHeroView__datetimeDateTime {
  display: flex;
  gap: 6px;
  align-items: end;
  height: 24px;

  font-size: 24px;
  white-space: nowrap;
  vertical-align: baseline;

  @container (width < 336px) {
    &[data-size="small"] {
      font-size: 7.5cqi;
    }
  }
}

.RunHeroView__datetimeDateTimeSeparator {
  position: relative;
  top: calc(-3.5px / 24px * 1em);
  font-size: calc(20px / 24px * 1em);
}

.RunHeroView__court {
  --iconColor: var(--localColorSecondaryFill);

  margin-top: 18px;
  color: var(--localColorSecondaryText);
}

.RunHeroView__corner {
  position: absolute;
  top: 4px;
  right: 6px;
}

.RunHeroView__footer {
  width: 100%;

  &:empty {
    display: none;
  }
}
</style>
